/**
 * The shape warhol's function tools return, read on BOTH sides of the
 * christo/carrie boundary.
 *
 * This module exists because the two readers used to live apart -- one in
 * `services/christo/app/handlers/catalyst/warholChat.ts`, one in
 * `frontends/carrie/src/routes/ask-puntt/components/truncatedJson.ts` -- and
 * their drifting apart is exactly what let a failed reviewer render as a
 * finished one. The contract belongs in one place, not copy-pasted either side
 * of the service/frontend line.
 *
 * The shapes, from warhol's own source:
 *
 * - `BudgetInResults` (`services/warhol/agents/budget.py`) merges a `budget`
 *   key into EVERY function-tool result, so a reader that does not take it
 *   back off is reading a different document than the tool returned.
 * - A MAPPING result just gains the key. A LIST result cannot carry one, so
 *   `with_budget` wraps it as `{result: [...], budget: {...}}` -- which is what
 *   `run_subagents` returns (a list of `SubagentReport`) on every real turn.
 */
// why: `without_budget` (`services/warhol/contracts/budget.py`) is the Python
// twin of `withoutWarholBudget` below; its docstring is the spec: "Anything
// without the key is handed back untouched, so a reader may call this on every
// return."
export const WARHOL_BUDGET_KEY = "budget";
export const WARHOL_RESULT_KEY = "result";

/**
 * christo's cap on a stored warhol tool-result summary.
 *
 * It lives here, beside the reader, because carrie has to assume it: a fan-out
 * whose report JSON exceeds this arrives as a cut-off document, and the cut
 * falls mid-JSON (and, because `budget` serializes AFTER the reports, almost
 * always removes that key too).
 */
export const WARHOL_STEP_SUMMARY_CHARS = 500;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A warhol tool return as the tool made it, with the budget note taken back off.
 *
 * A mapping result just gains the key; a list result -- and `run_subagents`
 * returns its reports as a list -- cannot take one, so `with_budget` wraps it
 * as `{result: [...], budget: {...}}`. Anything without a `budget` key comes
 * back untouched, so a caller may apply it to every return.
 */
export function withoutWarholBudget(value: unknown): unknown {
  if (!isRecord(value) || !(WARHOL_BUDGET_KEY in value)) return value;
  const keys = Object.keys(value);
  if (keys.length === 2 && WARHOL_RESULT_KEY in value) {
    return value[WARHOL_RESULT_KEY];
  }
  const rest: Record<string, unknown> = { ...value };
  delete rest[WARHOL_BUDGET_KEY];
  return rest;
}

/**
 * The report list in a `run_subagents` return, or null for anything else.
 *
 * The reports come back wrapped as `{result: [...], budget: {...}}`
 * (`withoutWarholBudget`), and this accepts the array under `result` whether
 * or not the note is still beside it -- carrie reads the SAME return through a
 * CAPPED summary, where the trailing `budget` key is already cut off, so keying
 * the unwrap on that key being present unwraps nothing on the real payload.
 * A bare list (limits unbound) is taken as-is.
 */
export function fanOutReports(parsed: unknown): unknown[] | null {
  const unwrapped = withoutWarholBudget(parsed);
  if (Array.isArray(unwrapped)) return unwrapped;
  if (isRecord(unwrapped) && Array.isArray(unwrapped[WARHOL_RESULT_KEY])) {
    return unwrapped[WARHOL_RESULT_KEY] as unknown[];
  }
  return null;
}

/** What stands in for a storage link whose path names no file. */
export const UNNAMED_FILE = "the file";

// Query parameters only a signed S3 request carries, SigV4 and SigV2 alike.
const SIGNED_PARAM =
  "(?:X-Amz-(?:Signature|Credential|Security-Token)|AWSAccessKeyId)=";
const SIGNED_QUERY = new RegExp(SIGNED_PARAM, "i");
const S3_HOST = /(?:^|\.)s3[.-](?:[a-z0-9-]+\.)*amazonaws\.com$/i;
// The `<digest>-` prefix warhol's `artifact_key` puts before a file's name.
const DIGEST_PREFIX = /^[0-9a-f]{16}-/;
// why: a link copied out of a document or printed by a script can carry a
// bracket or an apostrophe unencoded, which ends the plain match inside the
// file's name and would leave the signed query in the text. The optional tail
// runs the match on through that query, so the credentials go with the link.
// why the bridge is bounded: unbounded, every link in a long unbroken run
// re-read the rest of the run looking for a signed name. A name's remainder
// and the first parameters fit well inside the bound, and a signed parameter
// further off is still taken out as a stray one. It stops at a colon too, so
// it never runs on into the next link's `://`.
const SIGNED_TAIL = `(?:[^\\s<>"\`\\\\:]{0,256}?${SIGNED_PARAM}[^\\s<>"\`)\\]\\\\]*)?`;
// why each body is matched inside a lookahead and taken back by reference:
// that makes it atomic, which JavaScript has no possessive quantifier for. A
// body that gave characters back let the tail be tried again from every one
// of them, so a long link that ended without its `)` cost a tail attempt per
// character.
const STORAGE_URL = new RegExp(
  `(?:https?|s3)://(?=([^\\s<>()[\\]"'\`\\\\]+))\\1${SIGNED_TAIL}`,
  "gi",
);
// why the label excludes `[` as well as `]`: with `[` allowed, a text full of
// `[` restarted the scan at each one and re-read the rest.
const MARKDOWN_LINK = new RegExp(
  `\\[([^\\[\\]]*)\\]\\(\\s*<?((?:https?|s3)://(?=([^\\s)>]+))\\3${SIGNED_TAIL})>?\\s*\\)`,
  "gi",
);
const WHOLLY_A_LINK = /^(?:https?|s3):\/\/\S+$/i;
// A signed parameter left standing with no link in front of it: a link cut by
// a character the pattern never runs through, or by a space or a line break
// between the `&` and the name. Nothing signed stays, whatever cut it.
// why the separator is optional rather than searched for: a lazy scan from
// every `?`/`&` re-read the rest of a long unbroken run each time, and it
// never reached a name the cut had parted from its separator. The gap is two
// characters at most (a space, a line break): unbounded, a long run of spaces
// was re-read from each of them.
const STRAY_SIGNED_QUERY = new RegExp(
  `[?&]?\\s{0,2}${SIGNED_PARAM}[^\\s<>"\`)\\]\\\\]*`,
  "gi",
);
const TRAILING_PUNCTUATION = /[.,;:!?]+$/;

/**
 * True for an `s3://` address, a link to an S3 host, or any link signed the
 * way S3 signs. A signed link carries the signer's access key id and session
 * token, and `static.puntt.ai` is the product's own gated host, so it is NOT one.
 */
export function isStorageLink(url: string): boolean {
  if (/^s3:\/\//i.test(url)) return true;
  if (SIGNED_QUERY.test(url)) return true;
  try {
    return S3_HOST.test(new URL(url).hostname);
  } catch {
    return false;
  }
}

function storageFileName(url: string): string {
  // why not `new URL(url).pathname`: it rewrites a backslash into a slash, so
  // a name carrying one lost everything before it, and warhol's twin
  // (`urlsplit`) keeps the name whole.
  const path = url
    .replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i, "")
    .split(/[?#]/)[0];
  const last = path.slice(path.lastIndexOf("/") + 1);
  let name = last;
  try {
    name = decodeURIComponent(last);
  } catch {
    // A malformed escape is shown as it is.
  }
  return name.replace(DIGEST_PREFIX, "") || UNNAMED_FILE;
}

/**
 * `text` with every storage link replaced by the file's name.
 *
 * The twin of warhol's `agents/primary/links.py::scrub_storage_links`, for the
 * text christo stores and carrie shows: a tool result or a reply written
 * before warhol stopped handing out links still carries a presigned URL. A
 * markdown link keeps its words, as it does there, so a reply is not left
 * with a link that points at a bare file name.
 */
export function redactStorageLinks(text: string): string {
  return text
    .replace(MARKDOWN_LINK, (match, label: string, url: string) => {
      if (!isStorageLink(url)) return match;
      const name = storageFileName(url);
      const words = label.trim();
      if (!words || WHOLLY_A_LINK.test(words)) return name;
      return words.includes(name) ? words : `${words} (${name})`;
    })
    .replace(STORAGE_URL, (match) => {
      const tail = TRAILING_PUNCTUATION.exec(match)?.[0] ?? "";
      const url = tail ? match.slice(0, -tail.length) : match;
      return isStorageLink(url) ? storageFileName(url) + tail : match;
    })
    .replace(STRAY_SIGNED_QUERY, "");
}
